import { useLayoutEffect, useRef } from 'react';

const MIN_COL_WIDTH = 100;
const MAX_COL_WIDTH = 420;
// Columns at/below this width are never shrunk, so they stay readable.
const LOCK_BELOW_WIDTH = 120;
const CELL_H_PADDING = 24; // 12px each side

// Shared canvas for measuring text width (accurate for proportional fonts).
let measureCtx: CanvasRenderingContext2D | null = null;

const getMeasureContext = (sample: Element): CanvasRenderingContext2D | null => {
    if (!measureCtx) {
        measureCtx = document.createElement('canvas').getContext('2d');
    }
    if (measureCtx) {
        const cs = window.getComputedStyle(sample);
        measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    }
    return measureCtx;
};

// ideal = widest full line; minimum = widest single word (words never split).
const measureCellWidth = (
    text: string,
    ctx: CanvasRenderingContext2D
): { ideal: number; minimum: number } => {
    let ideal = 0;
    let minimum = 0;
    for (const line of text.split('\n')) {
        ideal = Math.max(ideal, ctx.measureText(line).width);
        for (const word of line.split(/\s+/)) {
            if (word) {
                minimum = Math.max(minimum, ctx.measureText(word).width);
            }
        }
    }
    return { ideal, minimum };
};

// Source of truth for column widths: [tableIndex][colIndex] -> px. Kept in a ref
// so dragged/measured widths survive the re-renders that reinject the HTML.
type WidthStore = Map<number, Map<number, number>>;

const computeColumnWidths = (
    headerCells: HTMLTableCellElement[],
    bodyRows: HTMLTableRowElement[],
    available: number,
    // Modal: uncapped widths + grow to fill leftover space. Chat: MAX_COL_WIDTH cap.
    fill = false
): number[] => {
    const ctx = getMeasureContext(headerCells[0]);
    if (!ctx) {
        return headerCells.map(() => MIN_COL_WIDTH);
    }

    const maxWidth = fill ? Number.POSITIVE_INFINITY : MAX_COL_WIDTH;
    const ideal: number[] = [];
    const floor: number[] = [];

    headerCells.forEach((th, colIndex) => {
        const head = measureCellWidth(th.textContent ?? '', ctx);
        let colIdeal = head.ideal;
        let colFloor = head.minimum;
        for (const row of bodyRows) {
            const cell = row.cells[colIndex];
            if (cell) {
                const m = measureCellWidth(cell.textContent ?? '', ctx);
                colIdeal = Math.max(colIdeal, m.ideal);
                colFloor = Math.max(colFloor, m.minimum);
            }
        }
        ideal.push(
            Math.max(MIN_COL_WIDTH, Math.min(maxWidth, colIdeal + CELL_H_PADDING))
        );
        floor.push(Math.max(MIN_COL_WIDTH, colFloor + CELL_H_PADDING));
    });

    const total = ideal.reduce((sum, w) => sum + w, 0);

    if (total <= available) {
        // Fits: chat keeps ideal widths; modal grows columns (weighted by width)
        // to fill the leftover space.
        if (!fill || available <= 0) {
            return ideal;
        }
        const extra = available - total;
        return ideal.map(w => w + (w / total) * extra);
    }

    if (available <= 0) {
        return ideal;
    }

    // Too wide: shrink only unlocked columns, proportional to slack above floor.
    const shrinkable = ideal
        .map((w, i) => ({ i, slack: w > LOCK_BELOW_WIDTH ? w - floor[i] : 0 }))
        .filter(c => c.slack > 0);
    const totalSlack = shrinkable.reduce((sum, c) => sum + c.slack, 0);
    if (totalSlack <= 0) {
        return ideal;
    }

    const excess = total - available;
    const widths = [...ideal];
    for (const { i, slack } of shrinkable) {
        const share = (slack / totalSlack) * excess;
        widths[i] = Math.max(floor[i], ideal[i] - share);
    }
    return widths;
};

// expand-05 from the Figma design system (node 8090:628405): four corner arrows.
const EXPAND_ICON_SVG =
    '<svg viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M6.5 3.5L9.5 0.5M9.5 3.5V0.5H6.5M3.5 3.5L0.5 0.5M3.5 0.5L0.5 0.5L0.5 3.5' +
    'M3.5 6.5L0.5 9.5M0.5 6.5L0.5 9.5H3.5M6.5 6.5L9.5 9.5M6.5 9.5H9.5V6.5" ' +
    'stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const setupTable = (
    table: HTMLTableElement,
    tableIndex: number,
    widthStore: WidthStore,
    onExpand: (table: HTMLTableElement) => void,
    cleanups: (() => void)[],
    fill: boolean
) => {
    const headerCells = Array.from(
        table.querySelectorAll<HTMLTableCellElement>('thead th')
    );
    if (headerCells.length === 0) {
        return;
    }

    const widths = widthStore.get(tableIndex) ?? new Map<number, number>();
    widthStore.set(tableIndex, widths);

    const bodyRows = Array.from(
        table.querySelectorAll<HTMLTableRowElement>('tbody tr')
    );

    const block = table.closest<HTMLElement>('.fl-table-block');

    // Measure once; stored widths (prior pass or drag) win, so resizes persist.
    if (widths.size < headerCells.length) {
        // Width available to the table: its container's content box. The chat
        // container is full-width (the reading column's max-width is applied to
        // text elements, not to tables), so the table fills to the inner-bg edge
        // and scrolls only when its natural width exceeds it.
        const box = block?.parentElement ?? block;
        const style = box ? window.getComputedStyle(box) : null;
        const padding = style
            ? parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
            : 0;
        const available = box ? box.clientWidth - padding : 0;
        const computed = computeColumnWidths(
            headerCells,
            bodyRows,
            available,
            // Fill available width in both the modal and the chat.
            true
        );
        computed.forEach((w, colIndex) => {
            if (!widths.has(colIndex)) {
                widths.set(colIndex, w);
            }
        });
    }

    // <colgroup> gives table-layout:fixed stable per-column widths to honor.
    let colgroup = table.querySelector('colgroup');
    if (!colgroup) {
        colgroup = document.createElement('colgroup');
        headerCells.forEach(() => {
            colgroup!.appendChild(document.createElement('col'));
        });
        table.insertBefore(colgroup, table.firstChild);
    }
    const cols = Array.from(colgroup.querySelectorAll('col'));

    const applyWidth = (colIndex: number) => {
        const w = widths.get(colIndex);
        if (w != null && cols[colIndex]) {
            cols[colIndex].style.width = `${w}px`;
        }
    };
    headerCells.forEach((_, colIndex) => applyWidth(colIndex));

    if (table.dataset.resizableReady === 'true') {
        // Handles already attached to this DOM node; <col> widths above suffice.
        return;
    }
    table.dataset.resizableReady = 'true';

    // Resize handle on a cell's right edge. startWidth reads the stored width
    // (not the rendered box) so the drag starts smoothly. Every cell in a column
    // shares colIndex, so a column resizes from any row, not just the header.
    const attachHandle = (cell: HTMLTableCellElement, colIndex: number) => {
        const handle = document.createElement('span');
        handle.className = 'fl-col-resize-handle';
        handle.setAttribute('aria-hidden', 'true');

        let startX = 0;
        let startWidth = 0;

        const onPointerMove = (e: PointerEvent) => {
            const delta = e.clientX - startX;
            const next = Math.max(MIN_COL_WIDTH, startWidth + delta);
            widths.set(colIndex, next);
            applyWidth(colIndex);
        };

        const onPointerUp = (e: PointerEvent) => {
            handle.releasePointerCapture(e.pointerId);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            table.classList.remove('fl-resizing');
        };

        const onPointerDown = (e: PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            startX = e.clientX;
            startWidth =
                widths.get(colIndex) ?? cell.getBoundingClientRect().width;
            handle.setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', onPointerUp);
            table.classList.add('fl-resizing');
        };

        handle.addEventListener('pointerdown', onPointerDown);
        cell.appendChild(handle);
        cleanups.push(() => {
            handle.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
        });
    };

    headerCells.forEach((th, colIndex) => attachHandle(th, colIndex));
    for (const row of bodyRows) {
        Array.from(row.cells).forEach((td, colIndex) =>
            attachHandle(td, colIndex)
        );
    }

    // Expand button on the (non-clipping) block, not in the modal where the
    // table is already expanded.
    if (!fill && block && !block.querySelector('.fl-table-expand')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fl-table-expand';
        // No native title (its tooltip overlaps the header); aria-label instead.
        button.setAttribute('aria-label', 'Expand table');
        button.innerHTML = EXPAND_ICON_SVG;

        const onClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onExpand(table);
        };
        button.addEventListener('click', onClick);
        block.appendChild(button);
        cleanups.push(() => button.removeEventListener('click', onClick));
    }
};

// Upgrades every table in the injected markdown: scroll wrapper, resizable
// columns, and (unless fill) an expand button. Widths persist across re-renders.
export const useResizableTables = (
    ref: React.RefObject<HTMLElement>,
    deps: unknown[],
    onExpand: (table: HTMLTableElement) => void,
    // Modal mode: grow columns to fill, no expand button.
    fill = false
) => {
    const widthStore = useRef<WidthStore>(new Map());
    const onExpandRef = useRef(onExpand);
    onExpandRef.current = onExpand;

    useLayoutEffect(() => {
        const root = ref.current;
        if (!root) {
            return;
        }

        const cleanups: (() => void)[] = [];
        const tables = Array.from(
            root.querySelectorAll<HTMLTableElement>('table')
        );
        tables.forEach((table, tableIndex) => {
            const parent = table.parentElement;
            if (parent && !parent.classList.contains('fl-table-scroll')) {
                // Block (no overflow) hosts the protruding expand button; inner
                // wrapper owns the horizontal scroll.
                const block = document.createElement('div');
                block.className = 'fl-table-block';
                const wrapper = document.createElement('div');
                wrapper.className = 'fl-table-scroll';
                parent.insertBefore(block, table);
                block.appendChild(wrapper);
                wrapper.appendChild(table);
            }
            setupTable(
                table,
                tableIndex,
                widthStore.current,
                t => onExpandRef.current(t),
                cleanups,
                fill
            );
        });

        return () => cleanups.forEach(fn => fn());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};
